import * as THREE from 'three';

/**
 * Basic geographic position
 */
export interface GeoPos {
  latitude: number;
  longitude: number;
}

/**
 * Options for computing a POI landing position from the path network
 */
export interface PoiLandingFromPathOptions {
  standoffDistance: number;
  eyeLevelOffset: number;
  maxDropFromCurrent?: number;
}

/**
 * Result of computing a POI landing position from the path network
 */
export interface PoiLandingFromPathResult {
  landingWorldPos: THREE.Vector3;
  approachDirWorldXZ: THREE.Vector3;
  closestPathPointGeo: GeoPos;
  usedPathDirection: boolean;
}

/**
 * Calculate distance between two points using Haversine formula
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Calculate bearing (direction) from point A to point B
 * @returns Bearing in degrees (0-360, where 0 is North)
 */
export function calculateBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);

  const θ = Math.atan2(y, x);
  const bearing = ((θ * 180) / Math.PI + 360) % 360;

  return bearing;
}

/**
 * Convert bearing to cardinal direction
 */
export function bearingToDirection(bearing: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(bearing / 45) % 8;
  return directions[index];
}

/**
 * Convert WGS84 to local scene coordinates
 * Uses simple equirectangular projection (good for small areas)
 */
export function wgs84ToLocal(
  lat: number,
  lon: number,
  alt: number,
  origin: [number, number, number]
): { x: number; y: number; z: number } {
  const [originLat, originLon, originAlt] = origin;

  // Earth radius in meters
  const R = 6371000;
  const latRad = (originLat * Math.PI) / 180;

  // Meters per degree at this latitude
  const metersPerDegLat = (Math.PI / 180) * R;
  const metersPerDegLon = (Math.PI / 180) * R * Math.cos(latRad);

  // Calculate offset in meters (local coordinates)
  const x = (lon - originLon) * metersPerDegLon;
  const y = (lat - originLat) * metersPerDegLat;
  const z = alt - originAlt;

  return { x, y, z };
}

/**
 * Calculate relative position of POI from camera for radar display
 * @returns Object with distance, bearing, and radar coordinates
 */
export function calculateRelativePosition(
  cameraLat: number,
  cameraLon: number,
  cameraHeading: number, // Camera's heading in degrees
  poiLat: number,
  poiLon: number
): {
  distance: number;
  bearing: number;
  relativeBearing: number; // Relative to camera heading
  radarX: number; // X position on radar (-1 to 1)
  radarY: number; // Y position on radar (-1 to 1)
  direction: string;
} {
  const distance = calculateDistance(cameraLat, cameraLon, poiLat, poiLon);
  const bearing = calculateBearing(cameraLat, cameraLon, poiLat, poiLon);

  // Relative bearing (how many degrees from where camera is looking)
  let relativeBearing = bearing - cameraHeading;
  if (relativeBearing < 0) relativeBearing += 360;
  if (relativeBearing > 360) relativeBearing -= 360;

  // Convert to radar coordinates (rotate so forward is up)
  const angleRad = (relativeBearing * Math.PI) / 180;
  const radarX = Math.sin(angleRad); // -1 (left) to 1 (right)
  const radarY = -Math.cos(angleRad); // -1 (up/forward) to 1 (down/back)

  const direction = bearingToDirection(bearing);

  return {
    distance,
    bearing,
    relativeBearing,
    radarX,
    radarY,
    direction
  };
}

/**
 * Check if POI is in front of camera (within a cone)
 */
export function isPOIAhead(relativeBearing: number, coneAngle: number = 45): boolean {
  // Forward is 0°, check if within ±coneAngle
  return relativeBearing <= coneAngle || relativeBearing >= (360 - coneAngle);
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1) {
    return `${Math.round(meters * 100)} cm`;
  } else if (meters < 1000) {
    return `${Math.round(meters)} m`;
  } else {
    return `${(meters / 1000).toFixed(1)} km`;
  }
}

/**
 * Get color for POI type
 */
export function getPOIColor(type: string): string {
  switch (type) {
    case 'POINT':
      return '#3B82F6'; // Blue
    case 'LINESTRING':
      return '#10B981'; // Green
    case 'POLYGON':
      return '#8B5CF6'; // Purple
    default:
      return '#6B7280'; // Gray
  }
}

/**
 * Convert lon/lat to local 2D planar coordinates for short-distance geometry.
 * Uses an equirectangular approximation around a local origin.
 */
function geoToLocalXY(
  lon: number,
  lat: number,
  originLon: number,
  originLat: number
): { x: number; y: number } {
  const metersPerDegLat = 111_320;
  const metersPerDegLon = 111_320 * Math.cos((originLat * Math.PI) / 180);

  return {
    x: (lon - originLon) * metersPerDegLon,
    y: (lat - originLat) * metersPerDegLat,
  };
}

/**
 * Convert local 2D planar coordinates back to lon/lat around a local origin.
 */
function localXYToGeo(
  x: number,
  y: number,
  originLon: number,
  originLat: number
): GeoPos {
  const metersPerDegLat = 111_320;
  const metersPerDegLon = 111_320 * Math.cos((originLat * Math.PI) / 180);

  return {
    longitude: originLon + x / metersPerDegLon,
    latitude: originLat + y / metersPerDegLat,
  };
}

/**
 * Compute the closest point on a 2D segment to a 2D point.
 */
function closestPointOnSegment2D(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): {
  x: number;
  y: number;
  t: number;
  abx: number;
  aby: number;
  abLenSq: number;
} {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;

  const abLenSq = abx * abx + aby * aby;
  const t = abLenSq > 1e-9 ? Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq)) : 0;

  return {
    x: ax + abx * t,
    y: ay + aby * t,
    t,
    abx,
    aby,
    abLenSq,
  };
}

/**
 * Compute a POI landing position using the nearest path segment in the path network.
 *
 * This does not require semantic understanding of the feature. It uses geometry only:
 * 1. Find nearest path segment to the POI
 * 2. Compute the local tangent of that path
 * 3. Compute the road-normal that points from the path toward the POI
 * 4. Place the landing point farther out on that same side
 *
 * pathNetwork format:
 *   number[][][] where each polyline is [[lon, lat], [lon, lat], ...]
 *
 * geoToWorld should convert a geographic point to a world-space THREE.Vector3.
 */
export function computePoiLandingFromPathNetwork(
  poiGeo: GeoPos,
  poiWorldPos: THREE.Vector3,
  cameraPos: THREE.Vector3,
  pathNetwork: number[][][],
  geoToWorld: (geo: GeoPos) => THREE.Vector3 | null,
  options: PoiLandingFromPathOptions
): PoiLandingFromPathResult | null {
  if (!pathNetwork?.length) return null;

  const originLon = poiGeo.longitude;
  const originLat = poiGeo.latitude;
  const poiXY = geoToLocalXY(poiGeo.longitude, poiGeo.latitude, originLon, originLat);

  let best:
    | {
        distSq: number;
        closestX: number;
        closestY: number;
        tangentX: number;
        tangentY: number;
      }
    | null = null;

  for (const polyline of pathNetwork) {
    if (!polyline || polyline.length < 2) continue;

    for (let i = 0; i < polyline.length - 1; i++) {
      const [lonA, latA] = polyline[i];
      const [lonB, latB] = polyline[i + 1];

      const a = geoToLocalXY(lonA, latA, originLon, originLat);
      const b = geoToLocalXY(lonB, latB, originLon, originLat);

      const cp = closestPointOnSegment2D(poiXY.x, poiXY.y, a.x, a.y, b.x, b.y);
      const dx = poiXY.x - cp.x;
      const dy = poiXY.y - cp.y;
      const distSq = dx * dx + dy * dy;

      if (!best || distSq < best.distSq) {
        best = {
          distSq,
          closestX: cp.x,
          closestY: cp.y,
          tangentX: cp.abx,
          tangentY: cp.aby,
        };
      }
    }
  }

  if (!best) return null;

  const tangentLen = Math.hypot(best.tangentX, best.tangentY);
  if (tangentLen < 1e-6) return null;

  const tx = best.tangentX / tangentLen;
  const ty = best.tangentY / tangentLen;

  // Two normals to the path tangent
  const n1x = -ty;
  const n1y = tx;
  const n2x = ty;
  const n2y = -tx;

  // Choose the normal that points from path toward the POI
  const pathToPoiX = poiXY.x - best.closestX;
  const pathToPoiY = poiXY.y - best.closestY;

  const dot1 = pathToPoiX * n1x + pathToPoiY * n1y;
  const dot2 = pathToPoiX * n2x + pathToPoiY * n2y;

  let nx = n1x;
  let ny = n1y;
  if (dot2 > dot1) {
    nx = n2x;
    ny = n2y;
  }

  // Place landing farther out from the POI on the same side of the road/path
  const landingGeo = localXYToGeo(
    poiXY.x + nx * options.standoffDistance,
    poiXY.y + ny * options.standoffDistance,
    originLon,
    originLat
  );

  const landingWorldBase = geoToWorld(landingGeo);
  if (!landingWorldBase) return null;

  const desiredY = poiWorldPos.y + options.eyeLevelOffset;
  const maxDropFromCurrent = options.maxDropFromCurrent ?? 2.0;
  const minAllowedY = cameraPos.y - maxDropFromCurrent;
  const landingY = Math.max(desiredY, minAllowedY);

  const landingWorldPos = new THREE.Vector3(
    landingWorldBase.x,
    landingY,
    landingWorldBase.z
  );

  const approachDirWorldXZ = new THREE.Vector3(
    poiWorldPos.x - landingWorldPos.x,
    0,
    poiWorldPos.z - landingWorldPos.z
  );

  if (approachDirWorldXZ.lengthSq() > 1e-6) {
    approachDirWorldXZ.normalize();
  }

  const closestPathPointGeo = localXYToGeo(best.closestX, best.closestY, originLon, originLat);

  return {
    landingWorldPos,
    approachDirWorldXZ,
    closestPathPointGeo,
    usedPathDirection: true,
  };
}